/**
 * PartialShipmentsTab.js
 *
 * Shared table for partial-shipment views (e.g. Drop Trailers).
 * Uses the same column manager, pinning, resize, reorder, and cell-expand
 * behavior as ShipmentsTable.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { MoreHorizontal } from "lucide-react";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import {
  addTab,
  setListViewState,
  setStatusFilter,
  setAdvancedFilters,
} from "../../../../redux/slices/shipmentSlice";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import { SORTABLE_COLUMNS, FILTER_TABS } from "./Utils/sortableColumns";

import {
  TABLE_HEADERS,
  DISABLED_STATUSES,
  makeKey,
  buildShipmentSearchPayload,
  formatShipmentRow,
  useShipmentFilters,
  AdvancedFilterToolbar,
  AdvancedFilterModal,
  DEFAULT_ADVANCED_FILTERS,
  DEFAULT_QUICK_FILTER,
} from "./shipmentShared";
import { useTableSavedViews, TableSavedViewsControl } from "../../../TableSavedViews";

// ─── Layout constants (aligned with ShipmentsTable) ───────────────────────────

const DATA_COLUMN_MIN_WIDTH    = 200;
const DATA_COLUMN_MAX_WIDTH    = 420;
const SELECTION_COLUMN_WIDTH   = 80;
const ACTION_COLUMN_WIDTH      = 50;
const SHIPMENT_NUMBER_HEADER   = "Shipment Number";

// ─── Main Component ───────────────────────────────────────────────────────────

const PartialShipmentsTab = ({
  config,
  searchQuery,
  filter,
  searchTrigger,
  setTotalShipments,
  userId,
  stateKey              = "partial-shipments",
  enableAdvancedFilters   = false,
}) => {
  const { moduleName, apiEndpoint, paginationLabel, filterType = FILTER_TABS.ALL, defaultPageSize = 50 } = config;

  const dispatch = useDispatch();
  const { listViewState = {} } = useSelector((s) => s.shipments);
  const savedViewState = listViewState[stateKey] || {};

  const [shipments,          setShipments]         = useState([]);
  const [isLoading,          setIsLoading]          = useState(false);
  const [selectedShipments,  setSelectedShipments]  = useState([]);
  const [currentPage,        setCurrentPage]        = useState(savedViewState.currentPage ?? 1);
  const [shipmentsPerPage,   setShipmentsPerPage]   = useState(savedViewState.shipmentsPerPage ?? defaultPageSize);
  const [openDropdownRow,    setOpenDropdownRow]    = useState(null);
  const [dropdownPos,        setDropdownPos]        = useState({ top: 0, left: 0 });
  const [totalPages,         setTotalPages]         = useState(1);
  const [expandedCellKey,    setExpandedCellKey]    = useState(null);
  const [showBulkMenu,       setShowBulkMenu]       = useState(false);
  const [showFilterModal,    setShowFilterModal]    = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  const [sortBy,    setSortBy]    = useState(savedViewState.sortBy    || null);
  const [ascending, setAscending] = useState(savedViewState.ascending ?? false);

  const dropdownRef       = useRef(null);
  const checkboxHeaderRef = useRef(null);
  const bulkMenuRef       = useRef(null);
  const prevFilterRef     = useRef(filter);
  const prevSearchTrigRef = useRef(searchTrigger);

  const token      = Cookies.get("authToken");
  const officeCode = Cookies.get("officeCodes");

  const { filters: advancedFilters, updateFilters, clearFilters, removePill, activeCount } =
    useShipmentFilters({ stateKey, persist: enableAdvancedFilters });

  const prevQuickFilterForDirtyRef = useRef(filter);
  const prevAdvancedFiltersForDirtyRef = useRef(JSON.stringify(advancedFilters));

  const tableViews = useTableSavedViews({
    pageKey: stateKey,
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", SHIPMENT_NUMBER_HEADER],
    getExtraConfig: () => ({
      sortBy,
      ascending,
      filter,
      advancedFilters: enableAdvancedFilters ? advancedFilters : undefined,
      currentPage,
      pageSize: shipmentsPerPage,
    }),
    onApplyConfig: (config, _viewMeta, options = {}) => {
      if (config.sortBy !== undefined) setSortBy(config.sortBy);
      if (config.ascending !== undefined) setAscending(config.ascending);
      if (config.currentPage) setCurrentPage(config.currentPage);
      if (config.pageSize) setShipmentsPerPage(config.pageSize);
      if (config.filter !== undefined) {
        dispatch(setStatusFilter(config.filter));
        dispatch(setListViewState({ stateKey, filter: config.filter }));
      }
      if (enableAdvancedFilters && config.advancedFilters !== undefined) {
        dispatch(setAdvancedFilters({
          stateKey,
          filters: config.advancedFilters ?? DEFAULT_ADVANCED_FILTERS,
        }));
      } else if (options.resetExtraConfig) {
        if (enableAdvancedFilters) {
          dispatch(setAdvancedFilters({ stateKey, filters: DEFAULT_ADVANCED_FILTERS }));
        }
        dispatch(setStatusFilter(DEFAULT_QUICK_FILTER));
        dispatch(setListViewState({ stateKey, filter: DEFAULT_QUICK_FILTER }));
      }
    },
  });

  const {
    columnWidths: savedColumnWidths,
    setColumnWidths: onColumnResize,
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    markDirty,
    reconcileDirtyState,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  useEffect(() => {
    if (!activeView || viewsLoading) return;

    const quickFilterChanged = prevQuickFilterForDirtyRef.current !== filter;
    const advancedFiltersJson = JSON.stringify(advancedFilters);
    const advancedFiltersChanged =
      prevAdvancedFiltersForDirtyRef.current !== advancedFiltersJson;

    prevQuickFilterForDirtyRef.current = filter;
    prevAdvancedFiltersForDirtyRef.current = advancedFiltersJson;

    if (!quickFilterChanged && !advancedFiltersChanged) return;

    reconcileDirtyState();
  }, [activeView, filter, advancedFilters, viewsLoading, reconcileDirtyState]);

  useEffect(() => {
    if (!isLoading) { setShowLoadingOverlay(false); return; }
    const t = setTimeout(() => setShowLoadingOverlay(true), 250);
    return () => clearTimeout(t);
  }, [isLoading]);

  useEffect(() => {
    dispatch(setListViewState({
      stateKey,
      currentPage,
      shipmentsPerPage,
      sortBy,
      ascending,
    }));
  }, [dispatch, stateKey, currentPage, shipmentsPerPage, sortBy, ascending]);

  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenDropdownRow(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  useEffect(() => {
    const handle = (e) => {
      if (
        bulkMenuRef.current       && !bulkMenuRef.current.contains(e.target) &&
        checkboxHeaderRef.current && !checkboxHeaderRef.current.contains(e.target)
      ) setShowBulkMenu(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const disabledByShipment = React.useMemo(() => {
    const m = new Map();
    for (const s of shipments)
      m.set(makeKey(s.shipmentNumber, s.officeCode), DISABLED_STATUSES.has((s.status || "").toUpperCase()));
    return m;
  }, [shipments]);

  const fetchShipments = useCallback(async () => {
    try {
      setIsLoading(true);
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
      if (officeCode) headers["Office-Code"] = officeCode;
      if (userId)     headers["userId"]      = userId.toString();

      const body = buildShipmentSearchPayload({
        searchQuery,
        filter,
        advancedFilters,
        sortBy,
        ascending,
        filterType,
        includeAdvancedFilters: enableAdvancedFilters,
      });

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}${apiEndpoint}?page=${currentPage - 1}&size=${shipmentsPerPage}`,
        body,
        { headers }
      );

      setShipments(data.content);
      setTotalShipments(data.totalElements);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error("Error fetching shipments:", err);
      setShipments([]);
      setTotalShipments(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [
    token, officeCode, userId, apiEndpoint,
    currentPage, shipmentsPerPage,
    searchQuery, filter, sortBy, ascending,
    advancedFilters, enableAdvancedFilters,
    filterType, setTotalShipments,
  ]);

  useEffect(() => {
    const filterChanged = prevFilterRef.current     !== filter;
    const searchChanged = prevSearchTrigRef.current !== searchTrigger;
    const needsReset    = (filterChanged || searchChanged) && currentPage !== 1;

    prevFilterRef.current     = filter;
    prevSearchTrigRef.current = searchTrigger;

    if (needsReset) { setCurrentPage(1); return; }
    fetchShipments();
  }, [searchTrigger, filter, currentPage, fetchShipments]);

  const handleSort = (newSortBy) => {
    setCurrentPage(1);
    if (sortBy !== newSortBy) { setSortBy(newSortBy); setAscending(true); }
    else setAscending((p) => !p);
    markDirty();
  };

  const formattedShipments = React.useMemo(
    () => shipments.map(formatShipmentRow),
    [shipments]
  );

  const handleSelectAll = (checked) => {
    if (!checked) { setSelectedShipments([]); return; }
    setSelectedShipments(
      formattedShipments
        .map((r) => makeKey(r["Shipment Number"], r["Office Code"]))
        .filter((key) => !disabledByShipment.get(key))
    );
  };

  const handleSingleCheckbox = (key) => {
    if (disabledByShipment.get(key)) return;
    setSelectedShipments((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleViewShipment = (shipmentNumber, shipmentOfficeCode) => {
    dispatch(addTab({
      id:                `view-${shipmentNumber}-${Date.now()}`,
      name:              `${shipmentNumber}`,
      contentType:       "shipmentView",
      shipmentNumber,
      shipmentOfficeCode,
      isDynamic:         true,
      createdAt:         Date.now(),
    }));
  };

  const handleActionClick = (key, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenDropdownRow((prev) => (prev === key ? null : key));
  };

  const renderActionColumn = (row, disabled, getStickyCellProps) => {
    const key    = makeKey(row["Shipment Number"], row["Office Code"]);
    const id     = row["Shipment Number"];
    const shipOC = row["Office Code"];
    const isOpen = openDropdownRow === key;
    const { className: stickyClass, style: stickyStyle } = getStickyCellProps("Action");

    const actions = disabled
      ? [{ icon: "Eye",   label: "View",   color: "blue",  action: () => handleViewShipment(id, shipOC) }]
      : [
          { icon: "Eye",   label: "View",   color: "blue",  action: () => handleViewShipment(id, shipOC) },
          { icon: "Edit",  label: "Edit",   color: "green", action: () => console.log("Edit",   id) },
          { icon: "Trash", label: "Delete", color: "red",   action: () => console.log("Delete", id) },
        ];

    return (
      <>
        <td
          onClick={(e) => e.stopPropagation()}
          className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${stickyClass}`}
          style={{
            ...stickyStyle,
            width: `${ACTION_COLUMN_WIDTH}px`, minWidth: `${ACTION_COLUMN_WIDTH}px`, maxWidth: `${ACTION_COLUMN_WIDTH}px`,
          }}
        >
          <button
            data-action-trigger
            className={`p-1 relative w-full transition-none ${
              disabled
                ? "opacity-60 hover:opacity-80"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text"
            }`}
            onClick={(e) => handleActionClick(key, e)}
          >
            <MoreHorizontal size={18} className="mx-auto" />
          </button>
        </td>

        {isOpen && ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
          >
            <ActionMenu
              rowId={key}
              isOpen
              onClose={() => setOpenDropdownRow(null)}
              moduleName="Shipment"
              actions={actions}
            />
          </div>,
          document.body
        )}
      </>
    );
  };

  const savedViewsControl = (
    <TableSavedViewsControl
      controlLabel={controlLabel}
      views={views}
      activeView={activeView}
      loading={viewsLoading}
      error={viewsError}
      isDirty={isDirty}
      allDataHeaders={TABLE_HEADERS.slice(2)}
      dataHeaders={dataHeaders}
      onSelectView={selectView}
      onSaveNew={saveNewView}
      onUpdateActive={updateActiveView}
      onRename={renameView}
      onDelete={deleteView}
      onSetDefault={setDefaultView}
      onUpdateColumnVisibility={updateColumnVisibility}
      onManageColumns={onManageColumns}
    />
  );

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">

      {enableAdvancedFilters ? (
        <AdvancedFilterToolbar
          filters={advancedFilters}
          activeCount={activeCount}
          onOpenModal={() => setShowFilterModal(true)}
          onClearAll={() => { clearFilters(); setCurrentPage(1); markDirty(); }}
          onRemovePill={(key) => { removePill(key); setCurrentPage(1); markDirty(); }}
          trailing={savedViewsControl}
        />
      ) : (
        <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
          {savedViewsControl}
        </div>
      )}

      <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none relative">
        <Table
          {...columnManagerProps}
          data={formattedShipments}
          initialColumnWidths={savedColumnWidths}
          onColumnResize={onColumnResize}
          onSelectAll={handleSelectAll}
          renderCheckboxActions={() => setShowBulkMenu((p) => !p)}
          checkboxHeaderRef={checkboxHeaderRef}
          selectedRows={selectedShipments}
          totalValidRows={formattedShipments.filter(
            (r) => !disabledByShipment.get(makeKey(r["Shipment Number"], r["Office Code"]))
          ).length}
          autoGrowColumns
          dataColumnMinWidth={DATA_COLUMN_MIN_WIDTH}
          dataColumnMaxWidth={DATA_COLUMN_MAX_WIDTH}
          enableColumnResize
          enableColumnReorder
          enableColumnPin
          nonHideableColumnIds={[SHIPMENT_NUMBER_HEADER]}
          sortBy={sortBy}
          ascending={ascending}
          onSort={(h) => { const k = SORTABLE_COLUMNS[h]; if (k) handleSort(k); }}
          sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
          sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
        >
          {(row, _rowIdx, columnWidths, getStickyCellProps, visibleColumns) => {
            const sn       = row["Shipment Number"];
            const key      = makeKey(sn, row["Office Code"]);
            const disabled = disabledByShipment.get(key);
            const { className: checkboxClass, style: checkboxStyle } = getStickyCellProps("Selection");
            const orderedDataHeaders = (visibleColumns ?? [])
              .map((col) => col.id)
              .filter((id) => id !== "Selection" && id !== "Action");
            const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;

            return (
              <>
                <td
                  className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${checkboxClass}`}
                  style={{
                    ...checkboxStyle,
                    width: `${SELECTION_COLUMN_WIDTH}px`, minWidth: `${SELECTION_COLUMN_WIDTH}px`, maxWidth: `${SELECTION_COLUMN_WIDTH}px`,
                  }}
                >
                  <input
                    type="checkbox"
                    className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                    checked={selectedShipments.includes(key)}
                    disabled={disabled}
                    onChange={() => handleSingleCheckbox(key)}
                  />
                </td>

                {renderActionColumn(row, disabled, getStickyCellProps)}

                {dataHeadersToRender.map((header) => {
                  const cellKey    = `${key}-${header}`;
                  const isExpanded = expandedCellKey === cellKey;
                  const { className: colClass, style: colStyle } = getStickyCellProps(header);

                  return (
                    <td
                      key={header}
                      className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${colClass}`}
                      onDoubleClick={() => handleViewShipment(sn, row["Office Code"])}
                      onMouseEnter={() => setExpandedCellKey(cellKey)}
                      onMouseLeave={() => setExpandedCellKey(null)}
                      style={{
                        ...colStyle,
                        whiteSpace:   isExpanded ? "normal"   : "nowrap",
                        overflow:     "hidden",
                        textOverflow: "ellipsis",
                        minWidth:     `${columnWidths?.[header] || DATA_COLUMN_MIN_WIDTH}px`,
                        maxWidth:     isExpanded ? `${DATA_COLUMN_MAX_WIDTH * 2}px` : `${DATA_COLUMN_MAX_WIDTH}px`,
                        width:        columnWidths?.[header] ? `${columnWidths[header]}px` : `${DATA_COLUMN_MIN_WIDTH}px`,
                        transition:   "max-width 120ms ease",
                      }}
                    >
                      {row[header]}
                    </td>
                  );
                })}
              </>
            );
          }}
        </Table>

        {showBulkMenu && checkboxHeaderRef.current && (
          <div
            ref={bulkMenuRef}
            className="absolute z-[1000]"
            style={{
              top:  checkboxHeaderRef.current.getBoundingClientRect().bottom + window.scrollY + 6,
              left: checkboxHeaderRef.current.getBoundingClientRect().left  + window.scrollX,
            }}
          >
            <ActionMenu
              rowId="bulk" isOpen onClose={() => setShowBulkMenu(false)}
              moduleName="Shipment" placement="header"
              actions={[
                { icon: "Check", label: "Approve Selected", color: "green", action: () => setShowBulkMenu(false) },
                { icon: "X",     label: "Reject Selected",  color: "red",   action: () => setShowBulkMenu(false) },
              ]}
            />
          </div>
        )}

        {showLoadingOverlay && (
          <div className="absolute inset-0 z-[2000] flex items-center justify-center bg-white/60 dark:bg-black/30">
            <Loader color="text-blue-600 dark:text-acs-dark-text" size="h-5 w-5" message={`Loading ${moduleName}...`} />
          </div>
        )}
      </div>

      <GlobalPagination
        label={paginationLabel}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={shipmentsPerPage}
        onItemsPerPageChange={setShipmentsPerPage}
      />

      {enableAdvancedFilters && showFilterModal && (
        <AdvancedFilterModal
          filters={advancedFilters}
          quickFilterStatus={filter}
          onApply={(f) => {
            updateFilters(f);
            if (f.statuses?.length) {
              dispatch(setStatusFilter(null));
              dispatch(setListViewState({ stateKey, filter: null }));
            }
            setCurrentPage(1);
            markDirty();
          }}
          onClose={() => setShowFilterModal(false)}
        />
      )}
    </div>
  );
};

export default PartialShipmentsTab;
